import type { LocationCoordinates } from './location';
import type { ItineraryItem } from './itinerary';
import type { Trip } from './trips';
import {
  calculateAllRouteOptions,
  calculateRoute,
  type RouteOption,
  type TravelMode,
} from './navigation';
import { haversineDistance } from '@/utils/geocodeHelpers';

export type MobilityMode = 'walk' | 'transit' | 'drive' | 'rideshare';

export type MobilityOption = {
  mode: MobilityMode;
  etaMinutes: number;
  costEstimate?: number;
  currency?: string;
  walkingMinutes?: number;
  distanceMeters?: number;
  safetyNote?: string;
  recommended: boolean;
  label: string;
  isEstimated: boolean;
  routeMode: TravelMode;
};

export type MobilityRecommendation = {
  options: MobilityOption[];
  recommended: MobilityOption | null;
  alternateCount: number;
};

const MODE_COST_USD: Record<MobilityMode, number> = {
  walk: 0,
  transit: 2.5,
  drive: 8,
  rideshare: 15,
};

const mapRouteMode = (mode: TravelMode): MobilityMode => {
  if (mode === 'walking') return 'walk';
  if (mode === 'transit') return 'transit';
  return 'drive';
};

const modeLabel = (mode: MobilityMode): string => {
  switch (mode) {
    case 'walk':
      return 'Walk';
    case 'transit':
      return 'Transit';
    case 'drive':
      return 'Drive';
    case 'rideshare':
      return 'Rideshare';
    default:
      return mode;
  }
};

const routeToOption = (
  route: RouteOption,
  currency: string,
  recommended: boolean,
): MobilityOption => {
  const mode = mapRouteMode(route.mode);
  const etaMinutes = clampEtaMinutes(route.duration / 60);
  return {
    mode,
    etaMinutes,
    costEstimate: MODE_COST_USD[mode],
    currency,
    walkingMinutes: mode === 'walk' ? etaMinutes : undefined,
    distanceMeters: route.distance,
    recommended,
    label: modeLabel(mode),
    isEstimated: mode === 'transit',
    routeMode: route.mode,
  };
};

const MAX_REASONABLE_ETA_MIN = 240;
const MAX_REASONABLE_KM = 80;

const clampEtaMinutes = (minutes: number): number =>
  Math.min(MAX_REASONABLE_ETA_MIN, Math.max(1, Math.round(minutes)));

export async function getMobilityRecommendation(
  origin: LocationCoordinates,
  destination: LocationCoordinates,
  currency = 'USD',
): Promise<MobilityRecommendation> {
  const routes = await calculateAllRouteOptions(origin, destination);
  const options: MobilityOption[] = routes.map((route, index) =>
    routeToOption(route, currency, index === 0),
  );

  if (routes.some((r) => r.mode === 'driving')) {
    const drive = routes.find((r) => r.mode === 'driving');
    if (drive && drive.distance > 1000) {
      options.push({
        mode: 'rideshare',
        etaMinutes: Math.max(1, Math.round(drive.duration / 60)),
        costEstimate: MODE_COST_USD.rideshare,
        currency,
        distanceMeters: drive.distance,
        recommended: false,
        label: 'Rideshare',
        isEstimated: true,
        routeMode: 'driving',
      });
    }
  }

  if (options.length === 0) {
    const km = haversineDistance(
      { lat: origin.latitude, lng: origin.longitude },
      { lat: destination.latitude, lng: destination.longitude },
    );
    if (km < 0.05) {
      return { options: [], recommended: null, alternateCount: 0 };
    }
    if (km > MAX_REASONABLE_KM) {
      return { options: [], recommended: null, alternateCount: 0 };
    }
    const walkMin = clampEtaMinutes((km / 5) * 60);
    const fallback: MobilityOption = {
      mode: 'walk',
      etaMinutes: walkMin,
      costEstimate: 0,
      currency,
      recommended: true,
      label: 'Walk',
      isEstimated: true,
      routeMode: 'walking',
      distanceMeters: km * 1000,
    };
    return { options: [fallback], recommended: fallback, alternateCount: 0 };
  }

  const sorted = [...options].sort((a, b) => a.etaMinutes - b.etaMinutes);
  sorted.forEach((o) => {
    o.recommended = false;
  });
  const transit = sorted.find((o) => o.mode === 'transit' && o.etaMinutes <= 45);
  const recommended = transit ?? sorted[0];
  recommended.recommended = true;

  return {
    options: sorted,
    recommended,
    alternateCount: Math.max(0, sorted.length - 1),
  };
}

export async function getMobilitySummaryLine(
  origin: LocationCoordinates,
  destination: LocationCoordinates,
  currency = 'USD',
): Promise<string | null> {
  const rec = await getMobilityRecommendation(origin, destination, currency);
  const best = rec.recommended;
  if (!best) return 'Get directions on map';
  if (best.etaMinutes >= MAX_REASONABLE_ETA_MIN) return 'Get directions on map';
  const cost =
    best.costEstimate != null && best.costEstimate > 0
      ? ` · ~$${best.costEstimate.toFixed(0)}`
      : '';
  const est = best.isEstimated ? ' (est.)' : '';
  return `Best now: ${best.label} · ${best.etaMinutes} min${cost}${est}`;
}

export function shouldShowGettingAround(
  trip: Trip,
  item: ItineraryItem,
  isTripActive: boolean,
): boolean {
  if (!item.location && !item.address) return false;
  if (isTripActive) return true;
  const now = Date.now();
  const itemTime = item.startDate.getTime();
  const hoursUntil = (itemTime - now) / (1000 * 60 * 60);
  return hoursUntil >= 0 && hoursUntil <= 24;
}

export async function estimateMobilityFromAddresses(
  origin: LocationCoordinates,
  destination: LocationCoordinates,
  preferredMode: TravelMode = 'walking',
  currency = 'USD',
): Promise<MobilityOption | null> {
  const route = await calculateRoute(origin, destination, preferredMode);
  if (!route) return null;
  return routeToOption(route, currency, true);
}
